import { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { useQuery } from "@tanstack/react-query"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"
import { MdDragIndicator } from "react-icons/md"
import { IoDocumentTextOutline, IoFolderOutline } from "react-icons/io5"
import MarkdownRenderer from "../markdown/MarkdownRenderer"
import XsdPreview, { JsonPreview } from "./XsdPreview"
import { fetchHrpDoc, fetchHrpDocBinary, fetchHrpDocs } from "../../utils/apiUtils"
import { loadHrpDocFromCacheOrNetwork } from "./hrpDocsCache"
import { buildHrpDocTree, matchHrpDoc, nodeAtPath } from "./hrpDocsTree"
import "./HrpDocs.scss"

export const HRP_DOCS_BROWSER = {
    title: "HRP Docs",
    basePath: "/hrp-docs",
    queryKey: "hrp-docs",
    fetchList: fetchHrpDocs,
    fetchFile: fetchHrpDoc,
    fetchBinary: fetchHrpDocBinary,
    keyPrefix: "hrp-docs/",
    listError: "Could not load HRP docs.",
}

const useHrpDocPreviewUrl = (data) => {
    const [url, setUrl] = useState(null)
    useLayoutEffect(() => {
        if (data?.blob) {
            const next = URL.createObjectURL(data.blob)
            setUrl(next)
            return () => URL.revokeObjectURL(next)
        }
        const remote = data?.url && !data.url.startsWith("blob:") ? data.url : null
        setUrl(remote)
        return undefined
    }, [data?.blob, data?.url])
    return url
}

const formatSize = (bytes) => {
    if (!bytes) return ""
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const S3DocsBrowser = ({
    title = HRP_DOCS_BROWSER.title,
    basePath = HRP_DOCS_BROWSER.basePath,
    queryKey = HRP_DOCS_BROWSER.queryKey,
    fetchList = HRP_DOCS_BROWSER.fetchList,
    fetchFile = HRP_DOCS_BROWSER.fetchFile,
    fetchBinary = HRP_DOCS_BROWSER.fetchBinary,
    keyPrefix = HRP_DOCS_BROWSER.keyPrefix,
    listError = HRP_DOCS_BROWSER.listError,
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [q, setQ] = useState("")
    const [forceRender, setForceRender] = useState(false)
    const escaped = basePath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const rest = decodeURIComponent(location.pathname.replace(new RegExp(`^${escaped}/?`), ""))
    const list = useQuery({
        queryKey: [queryKey],
        queryFn: fetchList,
        staleTime: 60_000,
    })
    const files = list.data?.files || []
    const { folder, file } = useMemo(() => matchHrpDoc(files, rest, keyPrefix), [files, rest, keyPrefix])
    useEffect(() => {
        setForceRender(false)
    }, [file?.key])
    const tree = useMemo(() => buildHrpDocTree(files), [files])
    const node = useMemo(() => nodeAtPath(tree, folder), [tree, folder])
    const crumbs = folder ? folder.split("/").filter(Boolean) : []

    const filteredFolders = Object.keys(node.folders)
        .sort((a, b) => a.localeCompare(b))
        .filter((name) => !q || name.toLowerCase().includes(q.toLowerCase()))
    const filteredFiles = node.files
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()))

    const doc = useQuery({
        queryKey: [queryKey, "file", file?.key, file?.lastModified],
        queryFn: () => loadHrpDocFromCacheOrNetwork(file, fetchFile, fetchBinary),
        enabled: Boolean(file?.key),
        staleTime: Infinity,
        gcTime: 30 * 60_000,
    })

    const previewUrl = useHrpDocPreviewUrl(doc.data)

    const go = (next) => navigate(next ? `${basePath}/${next}` : basePath)
    const openFolder = (name) => go(folder ? `${folder}/${name}` : name)
    const openFile = (item) => go(item.folder ? `${item.folder}/${item.name}` : item.name)

    const largeText = (doc.data?.text?.length || 0) > 400_000
    const showMarkdown = doc.data?.ext === "md" && (!largeText || forceRender)
    const showSchema = ["xsd", "xml", "wsdl"].includes(doc.data?.ext)
    const showJson = doc.data?.ext === "json" && !forceRender
    const isHtml = doc.data?.ext === "html" || doc.data?.ext === "htm"
    const showHtml = isHtml && !forceRender

    return (
        <div className="container hrp-docs">
            <PanelGroup
                autoSaveId="hrp-docs-nav"
                direction="horizontal"
                className="hrp-docs__split"
            >
            <Panel defaultSize={28} minSize={16} maxSize={50} className="hrp-docs__nav">
                <header className="hrp-docs__head">
                    <h1>{title}</h1>
                    <nav className="hrp-docs__crumbs">
                        <button type="button" onClick={() => go("")}>All</button>
                        {crumbs.map((part, i) => (
                            <span key={`${part}-${i}`}>
                                /
                                <button type="button" onClick={() => go(crumbs.slice(0, i + 1).join("/"))}>
                                    {part}
                                </button>
                            </span>
                        ))}
                    </nav>
                    <input
                        className="hrp-docs__search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter this folder"
                    />
                </header>
                <div className="hrp-docs__list">
                    {list.isLoading && <p className="hrp-docs__status">Loading folders…</p>}
                    {list.isError && <p className="hrp-docs__status">{listError}</p>}
                    {filteredFolders.map((name) => (
                        <button key={name} type="button" className="hrp-docs__row" onClick={() => openFolder(name)}>
                            <IoFolderOutline size="1.05rem" />
                            <strong>{name}</strong>
                        </button>
                    ))}
                    {filteredFiles.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={`hrp-docs__row${file?.key === item.key ? " is-active" : ""}`}
                            onClick={() => openFile(item)}
                        >
                            <IoDocumentTextOutline size="1.05rem" />
                            <strong>{item.name}</strong>
                            <em>{formatSize(item.size)}</em>
                        </button>
                    ))}
                    {!list.isLoading && !filteredFolders.length && !filteredFiles.length && (
                        <p className="hrp-docs__empty">Nothing in this folder.</p>
                    )}
                </div>
            </Panel>
            <PanelResizeHandle className="resize-handle hrp-docs__resize">
                <MdDragIndicator size="1.1rem" />
            </PanelResizeHandle>
            <Panel defaultSize={72} minSize={30} className="hrp-docs__preview">
                {!file && <p className="hrp-docs__empty">Open a folder, then a file to preview it here.</p>}
                {file && (
                    <>
                        <header className="hrp-docs__preview-head">
                            <strong>{file.name}</strong>
                            <span>
                                {file.ext.toUpperCase()}
                                {file.size ? ` · ${formatSize(file.size)}` : ""}
                                {doc.data?.fromCache ? " · saved locally" : ""}
                                {doc.data?.type === "url" && previewUrl && (
                                    <>
                                        {" · "}
                                        <a href={previewUrl} target="_blank" rel="noreferrer">Open</a>
                                    </>
                                )}
                                {largeText && doc.data?.ext === "md" && (
                                    <>
                                        {" · "}
                                        <button type="button" onClick={() => setForceRender((v) => !v)}>
                                            {forceRender ? "Show source" : "Render markdown"}
                                        </button>
                                    </>
                                )}
                                {doc.data?.ext === "json" && (
                                    <>
                                        {" · "}
                                        <button type="button" onClick={() => setForceRender((v) => !v)}>
                                            {forceRender ? "View JSON" : "Show source"}
                                        </button>
                                    </>
                                )}
                                {isHtml && (
                                    <>
                                        {" · "}
                                        <button type="button" onClick={() => setForceRender((v) => !v)}>
                                            {forceRender ? "Render HTML" : "Show source"}
                                        </button>
                                    </>
                                )}
                            </span>
                        </header>
                        {doc.isLoading && <p className="hrp-docs__status">Loading document…</p>}
                        {doc.isError && <p className="hrp-docs__status">Could not open this file.</p>}
                        {doc.data?.type === "url" && previewUrl && (
                            <iframe
                                key={previewUrl}
                                className="hrp-docs__frame"
                                title={file.name}
                                src={previewUrl}
                            />
                        )}
                        {doc.data?.type === "text" && (
                            <div className={`hrp-docs__preview-body${showSchema || showJson || showHtml ? " is-schema" : ""}`}>
                                {showMarkdown && <MarkdownRenderer>{doc.data.text}</MarkdownRenderer>}
                                {showSchema && <XsdPreview text={doc.data.text} ext={doc.data.ext} />}
                                {showJson && <JsonPreview text={doc.data.text} />}
                                {showHtml && <HtmlPreview text={doc.data.text} title={file.name} />}
                                {!showMarkdown && !showSchema && !showJson && !showHtml && (
                                    <pre className="hrp-docs__source">{doc.data.text}</pre>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Panel>
            </PanelGroup>
        </div>
    )
}

const HtmlPreview = ({ text, title }) => (
    <iframe
        className="hrp-docs__frame"
        title={title}
        sandbox=""
        srcDoc={text}
    />
)

const HrpDocs = () => <S3DocsBrowser {...HRP_DOCS_BROWSER} />

export default HrpDocs
